import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoAlertCircleOutline, IoDocumentTextOutline } from 'react-icons/io5';
import ClipLoader from 'react-spinners/ClipLoader';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Tooltip, Typography } from 'antd';
import { SideWithAsset } from 'shared-types';

import useSessionStore from 'hooks/stores/useSessionStore';
import useTrimboxCrop from 'hooks/useTrimboxCrop';

import {
  cancelQueuedSidePreview,
  enqueueSidePreviewGeneration,
  isCancelledSidePreviewError,
} from 'utils/sidePreviewQueue';
import {
  clearSidePreviewRequested,
  hasRequestedSidePreview,
  markSidePreviewRequested,
} from 'utils/sidePreviewRequests';

const { Text } = Typography;

// Hoe lang een pagina zonder preview zichtbaar moet zijn voordat we de preview aanvragen
// (zelfde gedrag als SortablePageItem in de book-layout).
const PREVIEW_VISIBILITY_TRIGGER_MS = 2000;

type Props = {
  side: SideWithAsset;
  pageNumber: number;
  width: number;
  height: number;
  isSelected: boolean;
  // Alle acties uit zolang er een insert/remove loopt.
  disabled: boolean;
  onOpen: (sideId: string) => void;
  // Blanco pagina invoegen óp deze positie; deze pagina en alles erna schuift naar rechts.
  onInsertBlankPage: () => void;
  // Pagina verwijderen. Niet gezet als verwijderen via de sessie-opties uit staat.
  onRemove?: () => void;
  // Blanco's die in deze sessie via de grid zijn ingevoegd (zoals in BookPreviewModal) mogen
  // zonder bevestiging weg; alle andere pagina's vragen eerst om bevestiging, want verwijderen
  // kan niet ongedaan worden gemaakt.
  isInsertedBlank: boolean;
};

const getThumbnailUrl = (side: SideWithAsset): string | undefined => {
  if (side.previewThumbnailUrl && side.previewThumbnailUrl !== '') return side.previewThumbnailUrl;
  if (side.previewImageUrl && side.previewImageUrl !== '') return side.previewImageUrl;
  return undefined;
};

const PageGridItem = ({
  side,
  pageNumber,
  width,
  height,
  isSelected,
  disabled,
  onOpen,
  onInsertBlankPage,
  onRemove,
  isInsertedBlank,
}: Props) => {
  const { t } = useTranslation();
  const sessionId = useSessionStore((state) => state.sessionId);
  const shouldShowQualityLabels = useSessionStore(
    (state) => state.session?.options?.ui?.sideList?.showQualityLabels !== false,
  );

  // Zelfde trimbox-uitsnede als BookPreviewModal: de bleed(-marker) rondom valt buiten het vak.
  const { hasValidPageSize, imageStyle: trimboxImageStyle } = useTrimboxCrop();

  const thumbnailUrl = getThumbnailUrl(side);
  const hasPreview = Boolean(thumbnailUrl);
  const [previewRequested, setPreviewRequested] = useState(() => hasRequestedSidePreview(side.id));
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  // force=false: automatische trigger (zichtbaarheid), vuurt maar één keer per side.
  // force=true: klik door de gebruiker, mag altijd opnieuw proberen zolang er geen preview is.
  // Verzoeken lopen via een queue met beperkte concurrency (zie utils/sidePreviewQueue.ts): het
  // grid kan tientallen pagina's tegelijk in beeld hebben en de server start per verzoek een
  // render-proces.
  const requestPreview = useCallback(
    (force: boolean): void => {
      if (hasPreview || isGeneratingPreview) return;
      if (!force && hasRequestedSidePreview(side.id)) return;
      markSidePreviewRequested(side.id);
      setPreviewRequested(true);
      setIsGeneratingPreview(true);
      enqueueSidePreviewGeneration(sessionId, side.id)
        .catch((error) => {
          // geannuleerd (uit beeld gescrold) of mislukt: opnieuw toestaan bij een volgende
          // zichtbaarheids-trigger of klik
          if (!isCancelledSidePreviewError(error)) {
            console.error('Side preview request failed', side.id, error);
          }
          clearSidePreviewRequested(side.id);
          if (isMountedRef.current) setPreviewRequested(false);
        })
        .finally(() => {
          if (isMountedRef.current) setIsGeneratingPreview(false);
        });
    },
    [hasPreview, isGeneratingPreview, sessionId, side.id],
  );

  // Zodra de echte preview via de websocket binnenkomt, vergeten we de aanvraag zodat een
  // latere vervanging van het asset opnieuw on-demand kan genereren.
  useEffect(() => {
    if (hasPreview) clearSidePreviewRequested(side.id);
  }, [hasPreview, side.id]);

  useEffect(() => {
    if (hasPreview || previewRequested) return;
    const node = nodeRef.current;
    if (!node) return;

    let visibilityTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          visibilityTimer = setTimeout(() => requestPreview(false), PREVIEW_VISIBILITY_TRIGGER_MS);
        } else if (visibilityTimer) {
          clearTimeout(visibilityTimer);
          visibilityTimer = null;
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);

    return () => {
      if (visibilityTimer) clearTimeout(visibilityTimer);
      observer.disconnect();
    };
  }, [hasPreview, previewRequested, requestPreview]);

  // Uit beeld gescrold of unmount terwijl het verzoek nog in de wachtrij stond: haal het eruit,
  // zodat de queue niet volloopt met pagina's die de gebruiker al voorbij is.
  useEffect(() => {
    if (hasPreview || !previewRequested) return;
    const node = nodeRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) cancelQueuedSidePreview(side.id);
      },
      { threshold: 0 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelQueuedSidePreview(side.id);
    };
  }, [hasPreview, previewRequested, side.id]);

  const hasWarnings =
    shouldShowQualityLabels &&
    Boolean(
      side.qualityCheck?.hasWarnings ||
        side.qualityCheck?.hasErrors ||
        side.asset?.metadata?.hasOverprint,
    );

  const handleOpen = (): void => {
    if (disabled) return;
    onOpen(side.id);
    requestPreview(true);
  };

  const renderContent = (): JSX.Element => {
    if (hasPreview) {
      return (
        <img
          src={thumbnailUrl}
          alt={`${t('Page')} ${pageNumber}`}
          loading="lazy"
          style={
            hasValidPageSize
              ? trimboxImageStyle
              : { width: '100%', height: '100%', objectFit: 'contain' }
          }
        />
      );
    }
    if (previewRequested || isGeneratingPreview) {
      return <ClipLoader color="#35b3ee" size={20} />;
    }
    return <IoDocumentTextOutline size={28} color="#B0B0B0" />;
  };

  const showInsertOverlay = isHovered && !disabled;
  // Verwijderknop: altijd zichtbaar op zelf ingevoegde blanco's, anders alleen bij hover.
  const showRemoveButton = Boolean(onRemove) && (isInsertedBlank || showInsertOverlay);

  const handleRemove = (): void => {
    if (!onRemove) return;
    if (isInsertedBlank) {
      onRemove();
      return;
    }
    Modal.confirm({
      title: t('BookLayout.DeletePageTitle', { page: pageNumber }),
      content: (
        <>
          <p style={{ marginBottom: 4 }}>{t('DeletePageConfirmation')}</p>
          <p style={{ marginBottom: 0, fontWeight: 600 }}>
            {t('BookLayout.DeletePageIrreversible')}
          </p>
        </>
      ),
      okText: t('Delete'),
      okButtonProps: { danger: true },
      cancelText: t('Cancel'),
      onOk: onRemove,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width }}>
      <Flex align="center" gap={4}>
        <Text style={{ fontSize: '0.8rem', fontWeight: 500, color: '#616161' }}>{pageNumber}</Text>
        {hasWarnings && (
          <IoAlertCircleOutline
            style={{ fontSize: '0.9rem', color: '#cf1322' }}
            title={t('PagesWithWarnings', { count: 1 }) as string}
          />
        )}
      </Flex>
      <div
        ref={nodeRef}
        role="button"
        tabIndex={0}
        title={t('BookLayout.OpenEditor') as string}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width,
          height,
          boxSizing: 'border-box',
          position: 'relative',
          backgroundColor: '#fff',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {renderContent()}

        {/* Hover-overlay: blanco pagina op deze plek invoegen (zoals in BookPreviewModal). De
            rest van de kaart blijft klikbaar om de editor te openen. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            opacity: showInsertOverlay ? 1 : 0,
            pointerEvents: showInsertOverlay ? 'auto' : 'none',
            transition: 'opacity 0.15s',
          }}
        >
          <Tooltip
            title={
              <>
                <div style={{ fontWeight: 600 }}>{t('BookPreview.InsertBlankPageHere')}</div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {t('BookPreview.InsertPageShiftHint')}
                </div>
              </>
            }
          >
            <Button
              shape="circle"
              type="primary"
              icon={<PlusOutlined />}
              aria-label={t('BookPreview.InsertBlankPageHere') as string}
              onClick={(event) => {
                event.stopPropagation();
                onInsertBlankPage();
              }}
              style={{ backgroundColor: '#35b3ee', borderColor: '#35b3ee' }}
            />
          </Tooltip>
        </div>

        {onRemove && (
          <Tooltip
            title={t(isInsertedBlank ? 'BookPreview.RemoveBlankPage' : 'BookLayout.DeletePage')}
          >
            <Button
              size="small"
              shape="circle"
              danger={!isInsertedBlank}
              icon={<DeleteOutlined />}
              disabled={disabled}
              aria-label={
                t(
                  isInsertedBlank ? 'BookPreview.RemoveBlankPage' : 'BookLayout.DeletePage',
                ) as string
              }
              onClick={(event) => {
                event.stopPropagation();
                handleRemove();
              }}
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 2,
                opacity: showRemoveButton ? 1 : 0,
                pointerEvents: showRemoveButton ? 'auto' : 'none',
                transition: 'opacity 0.15s',
              }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// Voorkomt dat elke websocket preview-update de hele grid laat re-renderen.
export default memo(PageGridItem);
